/**
 * "Fly to cart" micro-animation.
 *
 * Source (ProductCard / ProductDetail) dispatches a custom event with the
 * source image URL and the DOMRect of the triggering element. CartDrawer
 * listens for the event, creates a fixed-position clone of the image, and
 * animates it into the cart icon via the Web Animations API. Once the
 * animation finishes the cart icon itself gets a short pulse.
 *
 * Why an event rather than context: a custom event keeps ProductCard
 * ignorant of CartDrawer's internals and doesn't require any shared
 * provider tree.
 */

export const FLY_TO_CART_EVENT = "cart:flyto";

export interface FlyToCartDetail {
  sourceRect: DOMRect;
  imageUrl: string;
}

export function emitFlyToCart(detail: FlyToCartDetail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<FlyToCartDetail>(FLY_TO_CART_EVENT, { detail }),
  );
}

/**
 * Run the fly + pulse animation. Respects prefers-reduced-motion by
 * skipping the fly but still briefly pulsing the target so the user sees
 * that something happened.
 */
export function runFlyToCart(
  target: HTMLElement,
  detail: FlyToCartDetail,
): void {
  if (typeof window === "undefined") return;

  // Navbar mounts two CartDrawer instances (desktop + mobile) and only one
  // is visible at any time. Skip the hidden one so we don't get two parallel
  // animations, one ending at an offscreen icon.
  if (target.offsetParent === null) return;
  const targetRect = target.getBoundingClientRect();
  if (targetRect.width === 0 || targetRect.height === 0) return;

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  const targetCx = targetRect.left + targetRect.width / 2;
  const targetCy = targetRect.top + targetRect.height / 2;

  if (!prefersReducedMotion) {
    const { sourceRect, imageUrl } = detail;
    const size = 56;
    const node = document.createElement("img");
    node.src = imageUrl;
    node.alt = "";
    node.setAttribute("aria-hidden", "true");
    Object.assign(node.style, {
      position: "fixed",
      left: `${sourceRect.left + sourceRect.width / 2 - size / 2}px`,
      top: `${sourceRect.top + sourceRect.height / 2 - size / 2}px`,
      width: `${size}px`,
      height: `${size}px`,
      objectFit: "cover",
      borderRadius: "9999px",
      pointerEvents: "none",
      zIndex: "200",
      boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
      willChange: "transform, opacity",
    } as CSSStyleDeclaration);
    document.body.appendChild(node);

    const dx =
      targetCx - (sourceRect.left + sourceRect.width / 2);
    const dy =
      targetCy - (sourceRect.top + sourceRect.height / 2);

    const anim = node.animate(
      [
        { transform: "translate(0, 0) scale(1)", opacity: 1 },
        {
          transform: `translate(${dx * 0.5}px, ${dy - 60}px) scale(0.8)`,
          opacity: 0.95,
          offset: 0.6,
        },
        {
          transform: `translate(${dx}px, ${dy}px) scale(0.2)`,
          opacity: 0.2,
        },
      ],
      {
        duration: 700,
        easing: "cubic-bezier(0.34, 1.2, 0.64, 1)",
        fill: "forwards",
      },
    );
    anim.onfinish = () => node.remove();
    anim.oncancel = () => node.remove();
  }

  // Pulse the icon so the landing is visible. 250 ms total so it can't
  // be perceived as lag.
  target.animate(
    [
      { transform: "scale(1)" },
      { transform: "scale(1.25)" },
      { transform: "scale(1)" },
    ],
    { duration: 250, easing: "ease-out" },
  );
}
